/* Samsung Internet may recolour CSS even when the site declares a dark scheme.
   Keep real HTML text and use the image-paint method verified on the owner's phone.
   This enhancement is restricted to Samsung Internet and yields to accessibility modes. */
(() => {
  'use strict';
  const preview = new URLSearchParams(location.search).get('mm-colour-preview') === '1';
  if (!/SamsungBrowser\//.test(navigator.userAgent) && !preview) return;
  if (!CSS.supports('-webkit-background-clip','text') && !CSS.supports('background-clip','text')) return;
  const forced = matchMedia('(forced-colors: active)');
  const contrast = matchMedia('(prefers-contrast: more)');
  const root = document.documentElement;
  const cache = new Map();
  const painted = new Map();
  const excluded = 'script,style,noscript,svg,canvas,iframe,textarea,input,select,option,[contenteditable],mm-color-text,.shopify-payment-button,.jdgm-widget';
  const surfaceSelector = '.announcement,.site-header,.nav-dropdown,.mobile-nav,.mobile-nav__account,.mobile-nav__close,.header-search,.cart-count,.mm-btn,.badge,.quick-add,.newsletter-form button,.footer-newsletter button,.product-card,.product-card__body,.product-card__media,.trust-strip,.trust-item,.newsletter-bar,.footer,.cart-drawer,.info-drawer,.cart-drawer__head,.cart-drawer__foot,.info-head,.cart-line,.qty-btn,.cart-summary,.collection-toolbar,.filter-toggle,.product-thumbs button,.product-promise>div,.accordion,.accordion details,.shipping-progress,.shipping-progress span,.mm-sourcing-page,.mm-sourcing-page .primary,.mm-sourcing-page .nav-cta,.mm-sourcing-page .authentication,.mm-sourcing-page .auth-disclosure,.mm-sourcing-page .brief,.mm-sourcing-page .brief-top,.mm-sourcing-page .dot,.hero-sub,.sourcing-title-gold';
  const properties = ['background-image','background-size','background-clip','background-origin','background-color','border-color','border-image-source','border-image-slice'];
  function image(key, draw, width=64, height=64) {
    if (cache.has(key)) return cache.get(key);
    const canvas = document.createElement('canvas'); canvas.width=width; canvas.height=height;
    const ctx=canvas.getContext('2d'); if (!ctx) return null;
    draw(ctx,width,height);
    const value=`url("${canvas.toDataURL('image/png')}")`;
    if(cache.size<256) cache.set(key,value);
    return value;
  }
  const tile = color => image(color,(ctx,w,h)=>{ctx.fillStyle=color;ctx.fillRect(0,0,w,h);});
  const transparent = color => color==='transparent' || /rgba\([^)]*,\s*0(?:\.0+)?\)$/.test(color);
  function split(value) {
    let depth=0,start=0; const result=[];
    for(let i=0;i<value.length;i++){if(value[i]==='(')depth++;if(value[i]===')')depth--;if(value[i]===','&&!depth){result.push(value.slice(start,i).trim());start=i+1;}}
    result.push(value.slice(start).trim()); return result;
  }
  function gradient(value, rect) {
    if(!value.startsWith('linear-gradient(')) return null;
    const parts=split(value.slice(16,-1)); let angle=180;
    if(/deg$/.test(parts[0])) angle=parseFloat(parts.shift());
    else if(parts[0].startsWith('to ')){const dirs={'to right':90,'to left':270,'to top':0,'to bottom':180};if(!(parts[0] in dirs))return null;angle=dirs[parts.shift()];}
    const stops=parts.map(p=>p.match(/^(rgba?\([^)]*\)|#[0-9a-f]+|transparent)(?:\s+(\d+(?:\.\d+)?)%)?$/i));
    if(stops.length<2 || stops.some(s=>!s)) return null;
    const w=Math.max(1,Math.min(512,Math.round(rect.width))),h=Math.max(1,Math.min(512,Math.round(rect.height)));
    return image(value+w+'x'+h,(ctx,width,height)=>{
      const a=angle*Math.PI/180,dx=Math.sin(a),dy=-Math.cos(a),len=Math.abs(width*dx)+Math.abs(height*dy);
      const g=ctx.createLinearGradient(width/2-dx*len/2,height/2-dy*len/2,width/2+dx*len/2,height/2+dy*len/2);
      stops.forEach((s,i)=>g.addColorStop(s[2]===undefined?i/(stops.length-1):Math.min(1,Number(s[2])/100),s[1]));ctx.fillStyle=g;ctx.fillRect(0,0,width,height);
    },w,h);
  }
  function restore(el) {
    const old=painted.get(el);if(!old)return;
    for(const [name,value,priority] of old){if(value)el.style.setProperty(name,value,priority);else el.style.removeProperty(name);}
    painted.delete(el);
  }
  function surface(el) {
    restore(el);
    const s=getComputedStyle(el),rect=el.getBoundingClientRect(); if(!rect.width||!rect.height)return;
    const save=properties.map(p=>[p,el.style.getPropertyValue(p),el.style.getPropertyPriority(p)]);
    const layers=s.backgroundImage==='none'?[]:split(s.backgroundImage);
    const sizes=s.backgroundSize.split(/,(?![^()]*\))/).map(x=>x.trim());
    let changed=false;
    const images=layers.map((layer,i)=>{const converted=gradient(layer,rect);if(converted){sizes[i]='100% 100%';changed=true;}return converted||layer;});
    if(!transparent(s.backgroundColor)){images.push(tile(s.backgroundColor));sizes.push('auto');changed=true;}
    if(!changed)return;
    painted.set(el,save);
    const border=s.borderTopColor;
    const uniform=['Top','Right','Bottom','Left'].every(side=>parseFloat(s['border'+side+'Width'])>0&&s['border'+side+'Color']===border&&s['border'+side+'Style']==='solid');
    const opaque=/^rgb\(/.test(s.backgroundColor);
    if(uniform&&opaque&&!transparent(border)){
      images.push(tile(border));sizes.push('auto');
      el.style.backgroundClip=images.map((_,i)=>i===images.length-1?'border-box':'padding-box').join(',');
      el.style.backgroundOrigin=el.style.backgroundClip;el.style.borderColor='transparent';
    } else if(parseFloat(s.borderRadius)===0&&!transparent(border)&&['Top','Right','Bottom','Left'].every(side=>!parseFloat(s['border'+side+'Width'])||s['border'+side+'Color']===border)){
      el.style.borderImageSource=tile(border);el.style.borderImageSlice='1';
    }
    el.style.backgroundImage=images.join(',');el.style.backgroundSize=images.map((_,i)=>sizes[i]||sizes[0]||'auto').join(',');el.style.backgroundColor='transparent';
  }
  function texts(scope) {
    const walker=document.createTreeWalker(scope,NodeFilter.SHOW_TEXT);const nodes=[];
    while(walker.nextNode()) {const n=walker.currentNode;if(n.textContent.trim()&&n.parentElement&&!n.parentElement.closest(excluded))nodes.push(n);}
    for(const n of nodes){const p=n.parentElement,s=getComputedStyle(p);if(transparent(s.color)||s.backgroundClip==='text')continue;const wrap=document.createElement('mm-color-text');n.replaceWith(wrap);wrap.append(n);}
    scope.querySelectorAll('mm-color-text').forEach(el=>{const color=getComputedStyle(el.parentElement).color;if(!transparent(color))el.style.setProperty('--mm-text-paint',tile(color));});
  }
  let pending=false;
  const observer=new MutationObserver(()=>schedule());
  function run() {
    pending=false;observer.disconnect();
    if(forced.matches||contrast.matches){root.classList.remove('mm-samsung-colours');for(const el of [...painted.keys()])restore(el);}
    else {
      root.classList.add('mm-samsung-colours');
      for(const el of [...painted.keys()])if(!el.isConnected)painted.delete(el);
      document.querySelectorAll(surfaceSelector).forEach(surface);texts(document.body);
    }
    observer.observe(document.body,{childList:true,subtree:true,characterData:true,attributes:true,attributeFilter:['class','open','disabled']});
  }
  function schedule(){if(!pending){pending=true;requestAnimationFrame(run);}}
  // Repaint state-dependent colours without changing click handlers, form values or labels.
  document.addEventListener('pointerover',e=>{if(e.target.closest('a,button,summary'))schedule();},{passive:true});
  document.addEventListener('pointerout',e=>{if(e.target.closest('a,button,summary'))schedule();},{passive:true});
  document.addEventListener('focusin',schedule);document.addEventListener('focusout',schedule);
  let resizeTimer;window.addEventListener('resize',()=>{clearTimeout(resizeTimer);resizeTimer=setTimeout(schedule,150);},{passive:true});
  forced.addEventListener('change',schedule);contrast.addEventListener('change',schedule);
  run();
})();
